import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ExternalLink } from 'lucide-react';
import { useApi } from '../hooks/useApi';

interface Article {
  id: number;
  title: string;
  url: string;
  source: string;
  published_at: string | null;
  sentiment: number | null;
}

interface Signal {
  id: number;
  title: string;
  description?: string;
  severity: string;
  category: string;
  signal_type: string;
  detected_at: string;
}

const SEVERITY_TONE: Record<string, string> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'success',
};

function fmtDate(d?: string | null) {
  return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
}

/**
 * Reusable signal detail dialog: shows the signal and, crucially, its SOURCE articles
 * as outbound links — the click-through that was previously missing everywhere. Mount it
 * only while a signal is selected (it fetches on mount).
 */
export function SignalDetail({ signalId, onClose }: { signalId: number; onClose: () => void }) {
  const { data: signal } = useApi<Signal>(`/api/signals/${signalId}`, [signalId]);
  const { data: arts, loading } = useApi<{ articles: Article[]; count: number }>(
    `/api/signals/${signalId}/articles`,
    [signalId]
  );

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="halo-dialog-overlay" />
        <Dialog.Content className="halo-dialog-content">
          <div className="row-between" style={{ marginBottom: 'var(--space-4)' }}>
            <div>
              <span className="eyebrow" style={{ display: 'block', marginBottom: 'var(--space-1)' }}>Signal Detail</span>
              <Dialog.Title asChild>
                <h2 className="t-title-md">{signal?.title ?? `Signal #${signalId}`}</h2>
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="btn btn-tertiary btn-icon btn-sm" aria-label="Close">
                <X size={16} strokeWidth={1.75} />
              </button>
            </Dialog.Close>
          </div>

          {signal && (
            <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
              <span className="chip" data-tone={SEVERITY_TONE[signal.severity] || 'neutral'}>{signal.severity}</span>
              <span className="chip" data-tone="neutral">{signal.signal_type}</span>
              <span className="chip" data-tone="neutral">{(signal.category || '').replace(/_/g, ' ')}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
                {fmtDate(signal.detected_at)}
              </span>
            </div>
          )}

          {signal?.description && (
            <p className="t-body-sm" style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-5)', whiteSpace: 'pre-wrap' }}>
              {signal.description}
            </p>
          )}

          <hr style={{ borderColor: 'var(--color-border)', marginBottom: 'var(--space-4)' }} />

          <span className="t-label-sm" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
            Sources {arts ? `(${arts.count})` : ''}
          </span>

          {loading && <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>Loading sources…</p>}
          {arts && arts.articles.length === 0 && (
            <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>No source articles linked to this signal.</p>
          )}

          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            {arts?.articles.map((a) => (
              <a
                key={a.id}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="card"
                style={{ display: 'block', textDecoration: 'none' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{a.title || a.url}</div>
                    <div className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
                      {a.source}{a.published_at ? ` · ${fmtDate(a.published_at)}` : ''}
                    </div>
                  </div>
                  <ExternalLink size={14} strokeWidth={1.75} style={{ flexShrink: 0, color: 'var(--color-text-muted)' }} />
                </div>
              </a>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
